import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GetToken, GetAccountByTokenRaw } from "../../Backend/ApiCalls";
import "./PerfilComponent.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Notification } from "../NotificationLoginComponent";
import eyeIcon from "../../Assets/Images/ojoNMF.png";
import eyeSlashIcon from "../../Assets/Images/cerrar-ojoNMF.png";
import { BottomNav } from "../BottomNav";
import { getAgeTypeLabel } from "../../utils/ageType";
import { unlinkOneSignalUser } from "../../NotificationService";
import {
  addSecondaryProfileToken,
  clearProfileSession,
  getActiveProfileToken,
  getPrimaryProfileToken,
  getSecondaryProfileTokens,
  removeSecondaryProfileToken,
  setActiveProfileToken,
} from "../../utils/profileStorage";

const APP_VERSION_URL = "https://api.fallaplazarodrigo.es:999/App/Version";
const FALLA_APP_CURRENT_VERSION = "1.2.0";

const normalizeVersionPart = (part) => {
  const parsed = Number.parseInt(String(part || "0").replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const compareVersions = (current, target) => {
  const currentParts = String(current || "0").split(".").map(normalizeVersionPart);
  const targetParts = String(target || "0").split(".").map(normalizeVersionPart);
  const length = Math.max(currentParts.length, targetParts.length, 3);

  for (let index = 0; index < length; index += 1) {
    const currentValue = currentParts[index] || 0;
    const targetValue = targetParts[index] || 0;
    if (currentValue > targetValue) return 1;
    if (currentValue < targetValue) return -1;
  }

  return 0;
};

const isLikelyIos = () => {
  const platform = String(window?.Capacitor?.getPlatform?.() || "").toLowerCase();
  const userAgent = String(navigator?.userAgent || "").toLowerCase();
  return platform === "ios" || /iphone|ipad|ipod/.test(userAgent);
};

export const PerfilComponent = (props) => {
  const { user } = props;
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [principalProfile, setPrincipalProfile] = useState(null);
  const [currentProfileToken, setCurrentProfileToken] = useState(
    getActiveProfileToken()
  );

  const [showAddProfileModal, setShowAddProfileModal] = useState(false);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [isProfileSelectorClosing, setIsProfileSelectorClosing] = useState(false);
  const [isAddProfileClosing, setIsAddProfileClosing] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);

  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const openStore = async (storeUrl) => {
      if (!storeUrl) return;

      try {
        const browserModule = await import("@capacitor/browser").catch(() => null);
        if (browserModule?.Browser?.open) {
          await browserModule.Browser.open({ url: storeUrl });
          return;
        }
      } catch (error) {
        console.error("Error abriendo App Store:", error);
      }

      window.location.href = storeUrl;
    };

    const checkProfileUpdate = async () => {
      try {
        if (!isLikelyIos()) {
          return;
        }

        const response = await fetch(APP_VERSION_URL, { cache: "no-store" });
        if (!response.ok) {
          return;
        }

        const versionConfig = await response.json();
        const config = versionConfig?.ios;
        if (!config?.latestVersion || !config?.storeUrl) {
          return;
        }

        let installedVersion = FALLA_APP_CURRENT_VERSION;
        try {
          const { App } = await import("@capacitor/app");
          const appInfo = await App.getInfo();
          installedVersion = appInfo?.version || installedVersion;
        } catch {
          installedVersion = FALLA_APP_CURRENT_VERSION;
        }

        const minimumVersion = config.minimumVersion || config.latestVersion;
        const isBelowMinimum = compareVersions(installedVersion, minimumVersion) < 0;
        const isBehindLatest = compareVersions(installedVersion, config.latestVersion) < 0;
        const forceUpdate = isBelowMinimum || (Boolean(config.forceUpdate) && isBehindLatest);

        if (!isBehindLatest && !isBelowMinimum) return;

        const acceptedKey = `app-update-accepted-ios-${config.latestVersion}`;
        if (!forceUpdate && localStorage.getItem(acceptedKey) === "1") {
          return;
        }

        if (!isMounted) {
          return;
        }

        const wantsUpdate = window.confirm(
          [
            config.message || "Hay una nueva version de Falla Plaza Rodrigo disponible.",
            "",
            `Version instalada: ${installedVersion}`,
            `Nueva version: ${config.latestVersion}`,
            "",
            "Pulsa OK para ir a la App Store.",
          ].join("\n")
        );

        if (wantsUpdate) {
          localStorage.setItem(acceptedKey, "1");
          await openStore(config.storeUrl);
        }
      } catch (error) {
        console.error("Error comprobando actualizacion desde perfil:", error);
      }
    };

    checkProfileUpdate();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const principalToken = getPrimaryProfileToken();
    const activeToken = getActiveProfileToken();

    if (!principalToken) {
      setPrincipalProfile(null);
      return;
    }

    const loadPrincipalProfile = async () => {
      if (user && activeToken === principalToken) {
        setPrincipalProfile({
          name: user.name,
          fullName: `${user.name} ${user.surname}`,
          token: principalToken,
        });
        return;
      }

      try {
        const { response, data } = await GetAccountByTokenRaw(principalToken);

        if (!response.ok) {
          setPrincipalProfile(null);
          return;
        }

        setPrincipalProfile({
          name: data.name,
          fullName: `${data.name} ${data.surname}`,
          token: principalToken,
        });
      } catch (error) {
        console.error("Error al obtener el perfil principal:", error);
        setPrincipalProfile(null);
      }
    };

    loadPrincipalProfile();
  }, [user, currentProfileToken]);

  useEffect(() => {
    const fetchProfiles = async () => {
      const secondaryTokens = getSecondaryProfileTokens();

      if (!secondaryTokens.length) {
        setProfiles([]);
        return;
      }

      try {
        const profileResponses = await Promise.all(
          secondaryTokens.map(async (token) => {
            const { response, data } = await GetAccountByTokenRaw(token);

            if (!response.ok) {
              return null;
            }

            return {
              name: data.name,
              fullName: `${data.name} ${data.surname}`,
              token,
            };
          })
        );

        setProfiles(profileResponses.filter(Boolean));
      } catch (error) {
        console.error("Error al obtener perfiles secundarios:", error);
        setProfiles([]);
      }
    };

    fetchProfiles();
  }, []);

  // Función para generar un color basado en el nombre
  const generateColor = (name) => {
    const colors = [
      "var(--naranja-muyoscuro)",
      "var(--naranja-oscuro)",
      "var(--naranja-medio)",
      "var(--naranja-salmon)",
      "var(--gris)",
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const Avatar = ({ name }) => {
    const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");
    const backgroundColor = generateColor(name);
    return (
      <div className="avatar-profile" style={{ backgroundColor }}>
        <span className="avatar-initial">{getInitial(name)}</span>
      </div>
    );
  };

  const handleLogout = async () => {
    // Desvincula la suscripción push del usuario antes de salir, para que
    // este dispositivo deje de recibir sus notificaciones individuales.
    try {
      await unlinkOneSignalUser();
    } catch (e) {
      console.warn("No se pudo desvincular OneSignal en el logout:", e);
    }
    clearProfileSession();
    window.location.href = "/login";
  };

  const toggleProfileSelector = () => {
    if (showProfileSelector) {
      setIsProfileSelectorClosing(true);
      window.setTimeout(() => {
        setShowProfileSelector(false);
        setIsProfileSelectorClosing(false);
      }, 220);
      return;
    }

    setIsProfileSelectorClosing(false);
    setShowProfileSelector(true);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleProfileSwitch = (token, name) => {
    setActiveProfileToken(token);
    setCurrentProfileToken(token);
    setMessage(`Perfil cambiado a: ${name}`);
    setIsErrorNoti(false);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
      window.location.reload();
    }, 1500);
  };

  const handleAddProfile = async (event) => {
    event.preventDefault();
    setUsernameError("");
    setPasswordError("");
    setMessage("");

    const username = event.target.username.value;
    const password = event.target.password.value;

    try {
      const { response, json } = await GetToken(username, password);
      if (!response.ok) {
        if (json && json.Error) {
          const errorText = json.Error.toLowerCase();
          if (errorText.includes("usuario no encontrado")) {
            setUsernameError("Usuario incorrecto");
            setMessage("Usuario no encontrado");
          } else if (errorText.includes("contraseña incorrecta")) {
            setPasswordError("Contraseña incorrecta");
            setMessage("Contraseña incorrecta");
          } else {
            setMessage("Error al iniciar sesión con el nuevo perfil.");
          }
          setIsErrorNoti(true);
          setShowNotification(true);
          setTimeout(() => setShowNotification(false), 5000);
        } else {
          setMessage("Error al iniciar sesión con el nuevo perfil.");
          setIsErrorNoti(true);
          setShowNotification(true);
          setTimeout(() => setShowNotification(false), 5000);
        }
        return;
      }

      const newToken = String(json.loginToken || "").trim();
      const principalToken = getPrimaryProfileToken();
      const existingToken = newToken === principalToken || profiles.some((profile) => profile.token === newToken);
      if (existingToken) {
        setMessage("Este perfil ya ha sido agregado.");
        setIsErrorNoti(true);
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 5000);
        return;
      }

      // Añadimos el nuevo token a la lista de secundarios
      addSecondaryProfileToken(newToken);

      // Obtenemos la info del perfil recién añadido
      const profileResponse = await fetch(
        "https://api.fallaplazarodrigo.es:999/Users/GetAccountByToken",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: newToken,
          },
        }
      );
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfiles((prevProfiles) => {
          if (prevProfiles.some((profile) => profile.token === newToken)) {
            return prevProfiles;
          }

          return [
            ...prevProfiles,
            {
              name: profileData.name,
              fullName: `${profileData.name} ${profileData.surname}`,
              token: newToken,
            },
          ];
        });
      }

      setMessage("Perfil añadido con éxito.");
      setIsErrorNoti(false);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);

      // En vez de cerrar todo, animamos el cierre del modal "Añadir perfil"
      // y devolvemos al usuario al selector con la lista refrescada.
      // Reseteamos el form de paso para que no quede con datos antiguos.
      setShowPassword(false);
      setUsernameError("");
      setPasswordError("");
      try { event.target.reset(); } catch (_) { /* noop */ }

      setIsAddProfileClosing(true);
      window.setTimeout(() => {
        setShowAddProfileModal(false);
        setIsAddProfileClosing(false);
        setIsProfileSelectorClosing(false);
        setShowProfileSelector(true);
      }, 220);
    } catch (error) {
      console.error("Error al añadir nuevo perfil:", error);
      setMessage("Error al procesar el perfil.");
      setIsErrorNoti(true);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);
    }
  };

  const handleDeleteProfile = (token) => {
    const profile = profiles.find((p) => p.token === token);
    setShowProfileSelector(false);
    confirmAlert({
      title: "Confirmación de eliminación",
      message: `¿Estás seguro que quieres eliminar el perfil de ${profile.name}?`,
      buttons: [
        {
          label: "Sí",
          onClick: () => {
            removeSecondaryProfileToken(token);
            setProfiles(profiles.filter((p) => p.token !== token));
            setMessage("Perfil eliminado con éxito.");
            setIsErrorNoti(false);
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 5000);
          },
        },
        {
          label: "No",
          onClick: () => {
            setMessage("Operación cancelada.");
            setIsErrorNoti(true);
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 5000);
            setShowProfileSelector(true);
          },
        },
      ],
    });
  };

  // Construimos la lista de perfiles disponibles
  const availableProfiles = [
    ...(principalProfile && currentProfileToken !== principalProfile.token
      ? [principalProfile]
      : []),
    ...profiles.filter((profile) => profile.token !== currentProfileToken),
  ];

  return (
    <div className="perfil-component">
      {showNotification && (
        <Notification message={message} type={isErrorNoti ? "error" : "success"} />
      )}


      <BottomNav I18n={props.I18n} user={user} />

      <div className="info-message">
        <p>
          <strong>¡Nueva funcionalidad disponible!</strong> Ahora puedes gestionar
          múltiples perfiles y cambiar tus datos de forma rápida y sencilla.
        </p>
      </div>

      <button className="button-select-profile" onClick={toggleProfileSelector}>
        Seleccionar Perfil
      </button>

      {showProfileSelector && (
        <div className={`modal-profile-selector ${isProfileSelectorClosing ? 'is-closing' : ''}`}>
          <div className={`modal-content profile-selector-sheet ${isProfileSelectorClosing ? 'is-closing' : ''}`}>
            <div className="profile-selector-header">
              <h2>Seleccionar Perfil</h2>
              <div className="info-message profile-selector-note">
                <p>
                  Anade o cambia de perfil para gestionar tus cuentas de forma
                  personalizada.
                </p>
              </div>
            </div>

            <div className="profile-selector-body">
              <div className="current-profile-summary">
                <span className="current-profile-summary-label">Perfil activo</span>
                <strong>{user ? `${user.name} ${user.surname}` : "Perfil actual"}</strong>
              </div>

              <div className="profile-list">
                {availableProfiles.map((profile, index) => (
                  <div
                    key={index}
                    className="profile-item profile-card"
                    onClick={() =>
                      handleProfileSwitch(profile.token, profile.fullName)
                    }
                  >
                    <div className="profile-card-main">
                      <Avatar name={profile.name} />
                      <div className="profile-info">
                        <h4>{profile.name}</h4>
                        <p>{profile.fullName}</p>
                      </div>
                    </div>

                    <div className="profile-card-actions">
                      <button
                        className="button-select-profile profile-card-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProfileSwitch(profile.token, profile.fullName);
                        }}
                      >
                        Entrar
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProfile(profile.token);
                        }}
                        className="delete-button profile-card-delete"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-buttons profile-selector-footer">
              <button
                className="button-select-profile"
                onClick={toggleProfileSelector}
              >
                Volver
              </button>
              <button
                className="button-select-profile"
                onClick={() => {
                  setIsAddProfileClosing(false);
                  setShowAddProfileModal(true);
                  setShowProfileSelector(false);
                  setIsProfileSelectorClosing(false);
                }}
              >
                Anadir Nuevo Perfil
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddProfileModal && (
        <div className={`modal ${isAddProfileClosing ? 'is-closing' : ''}`}>
          <div className={`modal-content profile-selector-sheet add-profile-sheet ${isAddProfileClosing ? 'is-closing' : ''}`}>
            <div className="profile-selector-header">
              <h3>Anadir Nuevo Perfil</h3>
              <div className="info-message profile-selector-note">
                <p>
                  Inicia sesion con otro usuario para dejarlo disponible en este movil.
                </p>
              </div>
            </div>

            <form onSubmit={handleAddProfile}>
              <div className="profile-selector-body add-profile-body">
                <div className="input-container">
                  <label htmlFor="username">Usuario</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    className={`input-field ${usernameError ? "error" : ""}`}
                    placeholder="Ingresa el usuario"
                    required
                    onChange={() => setUsernameError("")}
                  />
                  {usernameError && <p className="error-message">{usernameError}</p>}
                </div>

                <div className="input-container">
                  <label htmlFor="password">Contrasena</label>
                  <div className="password-input-wrap">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      className={`input-field has-password-toggle ${passwordError ? "error" : ""}`}
                      placeholder="Ingresa la contrasena"
                      required
                      onChange={() => setPasswordError("")}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      <img
                        src={showPassword ? eyeIcon : eyeSlashIcon}
                        alt=""
                      />
                    </button>
                  </div>
                  {passwordError && <p className="error-message">{passwordError}</p>}
                </div>
              </div>

              <div className="modal-buttons profile-selector-footer">
                <button type="submit" className="button-select-profile">
                  Anadir
                </button>
                <button
                  type="button"
                  className="button-select-profile"
                  onClick={() => {
                    setIsAddProfileClosing(true);
                    window.setTimeout(() => {
                      setShowAddProfileModal(false);
                      setIsAddProfileClosing(false);
                      setIsProfileSelectorClosing(false);
                      setShowProfileSelector(true);
                    }, 220);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="recipient">
        <h1 className="titulo-page">{props.I18n.Profile}</h1>
        <section className="perfil-content">
          <p>
            {user ? user.name : "Nombre del usuario"}{" "}
            {user ? user.surname : "Apellido del usuario"}
          </p>
          <p>{user ? user.email : "Correo electrónico"}</p>
          <p>{user ? user.telephone : "Teléfono"}</p>
          <p>{user ? getAgeTypeLabel(user.ageType) : "Desconocido"}</p>
          <p>
            Recompensas:{" "}
            {(() => {
              try {
                const arr = Array.isArray(user?.rewards)
                  ? user.rewards
                  : typeof user?.rewards === "string"
                    ? JSON.parse(user.rewards || "[]")
                    : [];
                return arr.length ? arr.join(", ") : "Sin recompensas";
              } catch {
                return "Sin recompensas";
              }
            })()}
          </p>

          <p>
            Socios:{" "}
            {(() => {
              try {
                const arr = Array.isArray(user?.partners)
                  ? user.partners
                  : typeof user?.partners === "string"
                    ? JSON.parse(user.partners || "[]")
                    : [];
                return arr.length ? arr.join(", ") : "Sin socios";
              } catch {
                return "Sin socios";
              }
            })()}
          </p>


          <div className="perfil-buttons">
            <button className="button-default" onClick={() => navigate('/descuentos')}>
              Descuentos y QR
            </button>
            <button className="button-default" onClick={handleLogout}>
              Cerrar Sesión
            </button>
            <button
              className="button-default edit-button"
              onClick={() => navigate("/editar-perfil")}
            >
              Editar Perfil
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
